import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Leaf } from 'lucide-react';
import { useI18n } from '../hooks/useI18n';
import { getFileUrl, getBackendURL } from '../utils/assetUrlHelper';

const API_URL = getBackendURL();

// Card height to match Technologies section
const CARD_HEIGHT = '280px';

// Default variety images from scraped CISH data
const defaultVarietyImages = {
  'CISH-AMBIKA': '/images/ambika.jpg',
  'CISH-ARUNIKA': '/images/arunika.jpg',
  'AWADH-ABHAYA': '/images/abhaya.jpg',
  'AWADH-SAMRIDDHI': '/images/samriddhi.jpg',
  'ICAR-SAGARIKA': '/images/sagarika.png',
  // Additional varieties
  'SHWETA': '/images/ambika.jpg',
  'DHAWAL': '/images/ambika.jpg',
  'LALIMA': '/images/arunika.jpg',
  'BHUSHAN': '/images/abhaya.jpg',
  'JAMVANT': '/images/ambika.jpg',
  'SUCHITRA': '/images/arunika.jpg',
  'SUREKHA': '/images/abhaya.jpg',
  'ARPAN': '/images/ambika.jpg',
  'SHRESTHA': '/images/arunika.jpg',
};

export default function VarietiesCarousel({ varieties = [] }) {
  const { t } = useI18n();
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const autoScrollRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  // Auto-scroll functionality
  useEffect(() => {
    const startAutoScroll = () => {
      autoScrollRef.current = setInterval(() => {
        if (scrollRef.current && !isPaused) {
          const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
          // If we've scrolled to the end, reset to beginning
          if (scrollLeft >= scrollWidth - clientWidth - 10) {
            scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            // Scroll by one card width
            scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
          }
        }
      }, 3000); // Auto-scroll every 3 seconds
    };

    startAutoScroll();

    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    };
  }, [isPaused]);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      return () => el.removeEventListener('scroll', checkScroll);
    }
  }, [varieties]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = window.innerWidth < 640 ? 200 : 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Get image URL with fallback to local images
  // Get image URL with fallback logic
  const getImageUrl = (variety) => {
    // Try to get uploaded image first
    const uploadedUrl = getFileUrl(variety.image_url);
    
    if (uploadedUrl && variety.image_url) {
      // Check if it's HTTP (insecure) - use fallback
      if (variety.image_url.startsWith('http://')) {
        // Use local fallback for insecure URLs
        for (const [key, url] of Object.entries(defaultVarietyImages)) {
          if (variety.name?.toUpperCase().includes(key)) {
            return url;
          }
        }
        return '/images/variety-placeholder.jpg';
      }
      return uploadedUrl;
    }
    
    // Fallback to local images based on name
    for (const [key, url] of Object.entries(defaultVarietyImages)) {
      if (variety.name?.toUpperCase().includes(key)) {
        return url;
      }
    }
    return '/images/ambika.jpg';
  };

  if (!varieties || varieties.length === 0) return null;

  // Sort varieties by priority (highest first)
  const sortedVarieties = [...varieties].sort((a, b) => (b.priority || 0) - (a.priority || 0));
  
  // Only duplicate if we have few items (for smooth infinite scroll)
  // If more than 6 items, don't duplicate (causes confusion)
  const displayVarieties = sortedVarieties.length <= 6 
    ? [...sortedVarieties, ...sortedVarieties] 
    : sortedVarieties;

  return (
    <div 
      className="flex flex-col bg-[#FBFAF0] md:bg-transparent p-4 md:p-6 lg:p-0 pb-6" 
      style={{ minHeight: CARD_HEIGHT }}
      data-testid="varieties-carousel"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="flex-1 flex flex-col">
        {/* Section heading - matches CISH */}
        <div className="flex items-center gap-2 mb-4">
          <Leaf className="w-5 h-5 md:w-6 md:h-6 text-[#1B5E20] flex-shrink-0" aria-hidden="true" />
          <h2 className="text-base md:text-lg lg:text-xl font-bold text-[#1B5E20] uppercase leading-tight">
            VARIETIES FOR COMMERCIALIZATION
          </h2>
        </div>

        {/* Carousel container with scroll buttons - Increased height */}
        <div className="relative group flex-1" style={{ minHeight: '200px' }}>
          {/* Left scroll button - Hidden on mobile */}
          <button
            onClick={() => scroll('left')}
            className={`hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 transition-all ${
              canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-6 h-6 text-[#0E6D2B]" />
          </button>

          {/* Cards scroll container - Touch-friendly on mobile */}
          <div
            ref={scrollRef}
            className="flex gap-3 md:gap-4 overflow-x-auto pb-4 scroll-smooth px-1 -mx-1 snap-x snap-mandatory md:snap-none"
            style={{ 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none', 
              WebkitOverflowScrolling: 'touch',
              scrollPaddingLeft: '4px'
            }}
          >
            {displayVarieties.map((variety, index) => (
              <a
                key={`${variety.id}-${index}`}
                href="/varieties"
                className="flex-shrink-0 snap-center md:snap-align-none"
              >
                <div
                  className="shadow-md hover:shadow-xl overflow-hidden w-40 sm:w-44 md:w-48 lg:w-52 bg-white rounded-lg transition-transform hover:scale-[1.02] flex flex-col"
                  style={{ height: '200px' }}
                  data-testid={`variety-card-${index}`}
                >
                  <img
                    src={getImageUrl(variety)}
                    alt={variety.name}
                    className="w-full h-36 sm:h-40 object-cover flex-shrink-0"
                    loading="lazy"
                    onError={(e) => { e.target.src = '/images/ambika.jpg'; }}
                  />
                  <div className="bg-white p-2.5 text-center flex-1 flex items-center justify-center">
                    <div 
                      className="text-[10px] sm:text-xs md:text-sm leading-tight font-bold uppercase text-[#1B5E20] px-1"
                    >
                      {variety.name}
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>

          {/* Right scroll button - Hidden on mobile */}
          <button
            onClick={() => scroll('right')}
            className={`hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 transition-all ${
              canScrollRight ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            aria-label="Scroll right"
          >
            <ChevronRight className="w-6 h-6 text-[#0E6D2B]" />
          </button>
        </div>
      </div>

      {/* View More button - simplified */}
      <div className="mt-6">
        <a 
          href="/varieties" 
          className="bg-green-700 text-white px-6 py-3 rounded inline-block font-bold"
        >
          VIEW MORE →
        </a>
      </div>
    </div>
  );
}
